/**
 * KDTable — Data table matching OnMove-Admin DataTable style
 *
 * Features:
 * - Sortable column headers with ▲/▼ indicators
 * - Numbered pagination buttons (1, 2, 3…)
 * - "Showing X to Y of Z entries" + entries-per-page dropdown
 * - Row click handling
 * - Loading skeleton / empty state
 */

import { useState, useMemo } from "react";
import PropTypes from "prop-types";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Icon from "@mui/material/Icon";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Skeleton from "@mui/material/Skeleton";
import KDBox from "components/KDBox";
import KDTypography from "components/KDTypography";
import { useMaterialUIController } from "context";

/* ─── Head Cell ──────────────────────────────────────────── */
function HeadCell({ width, children, sorted, align }) {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  return (
    <KDBox
      component="th"
      width={width}
      py={1.5}
      px={3}
      sx={({ palette: { light }, borders: { borderWidth } }) => ({
        borderBottom: `${borderWidth[1]} solid ${light.main}`,
      })}
    >
      <KDBox
        position="relative"
        textAlign={align}
        color={darkMode ? "white" : "secondary"}
        opacity={0.7}
        sx={({ typography: { size, fontWeightBold } }) => ({
          fontSize: size.xxs,
          fontWeight: fontWeightBold,
          textTransform: "uppercase",
          cursor: sorted ? "pointer" : "default",
          userSelect: sorted ? "none" : "auto",
        })}
      >
        {children}
        {sorted && (
          <KDBox
            position="absolute"
            top={0}
            right={align !== "right" ? "16px" : 0}
            left={align === "right" ? "-5px" : "unset"}
            sx={({ typography: { size } }) => ({ fontSize: size.lg })}
          >
            <KDBox position="absolute" top={-6} color={sorted === "asce" ? "text" : "secondary"} opacity={sorted === "asce" ? 1 : 0.5}>
              <Icon>arrow_drop_up</Icon>
            </KDBox>
            <KDBox position="absolute" top={0} color={sorted === "desc" ? "text" : "secondary"} opacity={sorted === "desc" ? 1 : 0.5}>
              <Icon>arrow_drop_down</Icon>
            </KDBox>
          </KDBox>
        )}
      </KDBox>
    </KDBox>
  );
}

/* ─── Body Cell ──────────────────────────────────────────── */
function BodyCell({ noBorder, align, children }) {
  return (
    <KDBox
      component="td"
      textAlign={align}
      py={1.5}
      px={3}
      sx={({ palette: { light }, typography: { size }, borders: { borderWidth } }) => ({
        fontSize: size.sm,
        borderBottom: noBorder ? "none" : `${borderWidth[1]} solid ${light.main}`,
      })}
    >
      <KDBox display="inline-block" width="max-content" color="text" sx={{ verticalAlign: "middle" }}>
        {children}
      </KDBox>
    </KDBox>
  );
}

/* ─── Pagination Item ────────────────────────────────────── */
function PaginationItem({ active, children, onClick }) {
  return (
    <KDBox
      component="button"
      onClick={onClick}
      display="flex"
      alignItems="center"
      justifyContent="center"
      sx={({ palette }) => ({
        width: 36,
        height: 36,
        borderRadius: "50%",
        border: "none",
        cursor: "pointer",
        fontWeight: active ? 700 : 400,
        fontSize: "0.875rem",
        color: active ? "#fff" : palette.text.secondary,
        background: active
          ? `linear-gradient(135deg, ${palette.info.main}, ${palette.info.state || palette.info.main})`
          : "transparent",
        "&:hover": {
          background: active ? undefined : palette.action.hover,
        },
      })}
    >
      {children}
    </KDBox>
  );
}

/* ═══ KDTable ════════════════════════════════════════════════ */
function KDTable({
  columns,
  rows,
  loading,
  emptyMessage,
  onRowClick,
  defaultSortField,
  defaultSortDirection,
  rowsPerPageOptions,
}) {
  const entriesOptions = (rowsPerPageOptions || [10, 25, 50, 100]).map(String);
  const [sortField, setSortField] = useState(defaultSortField || "");
  const [sortDirection, setSortDirection] = useState(defaultSortDirection || "asc");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(Number(entriesOptions[0]) || 10);

  /* sorting */
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const sortedRows = useMemo(() => {
    if (!sortField) return rows;
    return [...rows].sort((a, b) => {
      const aVal = a[sortField];
      const bVal = b[sortField];
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      if (typeof aVal === "string") {
        return sortDirection === "asc" ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      return sortDirection === "asc" ? aVal - bVal : bVal - aVal;
    });
  }, [rows, sortField, sortDirection]);

  /* pagination math */
  const totalPages = Math.ceil(sortedRows.length / pageSize);
  const paginatedRows = useMemo(() => {
    const start = page * pageSize;
    return sortedRows.slice(start, start + pageSize);
  }, [sortedRows, page, pageSize]);

  const entriesStart = sortedRows.length === 0 ? 0 : page * pageSize + 1;
  const entriesEnd = Math.min((page + 1) * pageSize, sortedRows.length);

  const getSortedValue = (field) => {
    if (sortField !== field) return "none";
    return sortDirection === "asc" ? "asce" : "desc";
  };

  /* loading skeleton */
  if (loading) {
    return (
      <KDBox p={2}>
        {[...Array(6)].map((_, i) => (
          <Skeleton key={i} height={48} sx={{ mb: 0.5, borderRadius: 1 }} />
        ))}
      </KDBox>
    );
  }

  /* empty */
  if (!rows.length) {
    return (
      <KDBox display="flex" justifyContent="center" alignItems="center" py={6}>
        <KDTypography variant="body2" color="text">{emptyMessage}</KDTypography>
      </KDBox>
    );
  }

  return (
    <TableContainer sx={{ boxShadow: "none" }}>
      {/* Table */}
      <Table>
        <KDBox component="thead">
          <TableRow>
            {columns.map((col) => (
              <HeadCell
                key={col.field}
                width={col.width || "auto"}
                align={col.align || "left"}
                sorted={col.sortable !== false ? getSortedValue(col.field) : false}
              >
                <KDBox
                  onClick={col.sortable !== false ? () => handleSort(col.field) : undefined}
                  sx={{ cursor: col.sortable !== false ? "pointer" : "default" }}
                >
                  {col.header}
                </KDBox>
              </HeadCell>
            ))}
          </TableRow>
        </KDBox>

        <KDBox component="tbody">
          {paginatedRows.map((row, rowIndex) => (
            <TableRow
              key={row.id || rowIndex}
              hover
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              sx={{ cursor: onRowClick ? "pointer" : "default" }}
            >
              {columns.map((col) => (
                <BodyCell
                  key={col.field}
                  align={col.align || "left"}
                  noBorder={rowIndex === paginatedRows.length - 1}
                >
                  {col.render ? col.render(row[col.field], row) : row[col.field]}
                </BodyCell>
              ))}
            </TableRow>
          ))}
        </KDBox>
      </Table>

      {/* Bottom bar: "Showing X to Y of Z" | pagination | entries per page */}
      <KDBox
        display="flex"
        flexDirection={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        p={3}
        gap={2}
      >
        {/* Left: entries info */}
        <KDTypography variant="button" color="secondary" fontWeight="regular">
          Showing {entriesStart} to {entriesEnd} of {sortedRows.length} entries
        </KDTypography>

        {/* Right: pagination + entries dropdown */}
        <KDBox display="flex" alignItems="center" gap={2} flexWrap="wrap" justifyContent="flex-end">
          {/* Numbered pagination */}
          {totalPages > 1 && (
            <KDBox display="flex" alignItems="center" gap={0.5}>
              {page > 0 && (
                <PaginationItem onClick={() => setPage((p) => p - 1)}>
                  <Icon sx={{ fontWeight: "bold" }}>chevron_left</Icon>
                </PaginationItem>
              )}
              {totalPages <= 7 ? (
                [...Array(totalPages)].map((_, i) => (
                  <PaginationItem key={i} active={page === i} onClick={() => setPage(i)}>
                    {i + 1}
                  </PaginationItem>
                ))
              ) : (
                <KDBox width="4.5rem" mx={1}>
                  <TextField
                    size="small"
                    type="number"
                    inputProps={{ min: 1, max: totalPages }}
                    value={page + 1}
                    onChange={(e) => {
                      const v = Number(e.target.value);
                      if (v >= 1 && v <= totalPages) setPage(v - 1);
                    }}
                  />
                </KDBox>
              )}
              {page < totalPages - 1 && (
                <PaginationItem onClick={() => setPage((p) => p + 1)}>
                  <Icon sx={{ fontWeight: "bold" }}>chevron_right</Icon>
                </PaginationItem>
              )}
            </KDBox>
          )}

          {/* Entries per page */}
          <KDBox display="flex" alignItems="center" gap={1}>
            <Autocomplete
              disableClearable
              value={String(pageSize)}
              options={entriesOptions}
              onChange={(_e, val) => { setPageSize(Number(val)); setPage(0); }}
              size="small"
              sx={{ width: "5rem" }}
              renderInput={(params) => <TextField {...params} />}
            />
            <KDTypography variant="caption" color="secondary">
              entries per page
            </KDTypography>
          </KDBox>
        </KDBox>
      </KDBox>
    </TableContainer>
  );
}

KDTable.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      field: PropTypes.string.isRequired,
      header: PropTypes.string.isRequired,
      align: PropTypes.oneOf(["left", "center", "right"]),
      width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      sortable: PropTypes.bool,
      render: PropTypes.func,
    })
  ).isRequired,
  rows: PropTypes.array.isRequired,
  loading: PropTypes.bool,
  emptyMessage: PropTypes.string,
  onRowClick: PropTypes.func,
  defaultSortField: PropTypes.string,
  defaultSortDirection: PropTypes.oneOf(["asc", "desc"]),
  rowsPerPageOptions: PropTypes.arrayOf(PropTypes.number),
};

KDTable.defaultProps = {
  loading: false,
  emptyMessage: "No data available",
  onRowClick: null,
  defaultSortField: "",
  defaultSortDirection: "asc",
  rowsPerPageOptions: [10, 25, 50, 100],
};

export default KDTable;
